import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  CoinsIcon, 
  PiggyBankIcon, 
  ShoppingBagIcon, 
  TrophyIcon,
  SpaceIcon,
  InfoIcon,
  ArrowUpIcon,
  SaveIcon
} from "lucide-react";

// Game constants and level configuration
const BASE_GAME_SPEED = 5;
const GRAVITY = 0.6;
const JUMP_FORCE = -10;
const BASE_OBSTACLE_FREQUENCY = 2000;
const BASE_COIN_FREQUENCY = 1500;
const MAX_LEVEL = 5; // Added constant for max level

interface LevelConfig {
  requiredCoins: number;
  speed: number;
  obstacleFrequency: number;
  coinFrequency: number;
}

const LEVELS: Record<number, LevelConfig> = {
  1: {
    requiredCoins: 50,
    speed: BASE_GAME_SPEED,
    obstacleFrequency: BASE_OBSTACLE_FREQUENCY,
    coinFrequency: BASE_COIN_FREQUENCY
  },
  2: {
    requiredCoins: 100,
    speed: BASE_GAME_SPEED * 1.2,
    obstacleFrequency: BASE_OBSTACLE_FREQUENCY * 0.9,
    coinFrequency: BASE_COIN_FREQUENCY * 0.9
  },
  3: {
    requiredCoins: 200,
    speed: BASE_GAME_SPEED * 1.4,
    obstacleFrequency: BASE_OBSTACLE_FREQUENCY * 0.8,
    coinFrequency: BASE_COIN_FREQUENCY * 0.8
  },
  4: {
    requiredCoins: 300,
    speed: BASE_GAME_SPEED * 1.6,
    obstacleFrequency: BASE_OBSTACLE_FREQUENCY * 0.7,
    coinFrequency: BASE_COIN_FREQUENCY * 0.7
  },
  5: {
    requiredCoins: 500,
    speed: BASE_GAME_SPEED * 1.8,
    obstacleFrequency: BASE_OBSTACLE_FREQUENCY * 0.6,
    coinFrequency: BASE_COIN_FREQUENCY * 0.6
  }
};

interface GameObject {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface Player extends GameObject {
  velocityY: number;
}

interface BudgetCategory {
  name: string;
  allocation: number;
}

export const SavingsSprintGame = () => {
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [showTutorial, setShowTutorial] = useState(true);
  const [currentLevel, setCurrentLevel] = useState(1);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [player, setPlayer] = useState<Player>({
    x: 50,
    y: 340, // Adjusted from 300 to match the ground
    width: 40,
    height: 40,
    velocityY: 0,
  });
  const [obstacles, setObstacles] = useState<GameObject[]>([]);
  const [coins, setCoins] = useState<GameObject[]>([]);
  const [isJumping, setIsJumping] = useState(false);
  const [showBudgetExercise, setShowBudgetExercise] = useState(false);
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategory[]>([
    { name: "Savings", allocation: 0 },
    { name: "Food", allocation: 0 },
    { name: "Entertainment", allocation: 0 },
    { name: "Education", allocation: 0 }
  ]);

  // Get current level configuration
  const currentLevelConfig = LEVELS[currentLevel];

  // Calculate progress to next level
  const levelProgress = (score / currentLevelConfig.requiredCoins) * 100;

  // Check for level up
  useEffect(() => {
    if (!gameStarted || gameOver) return;

    if (score >= currentLevelConfig.requiredCoins && currentLevel < Object.keys(LEVELS).length) {
      setGameStarted(false);
      setShowBudgetExercise(true); // Show budget exercise instead of level up modal directly
      toast.success(`Level ${currentLevel} Complete! 🎉`);
    }
  }, [score, currentLevel, gameStarted, gameOver]);

  const handleJump = useCallback(() => {
    if (!gameStarted) return;
    
    if (!isJumping) {
      setPlayer(prev => ({
        ...prev,
        velocityY: JUMP_FORCE
      }));
      setIsJumping(true);
    }
  }, [gameStarted, isJumping]);

  // Handle keyboard controls
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        e.preventDefault();
        handleJump();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleJump]);

  // Game loop with updated speeds
  useEffect(() => {
    if (!gameStarted) return;

    const gameLoop = setInterval(() => {
      // Update player position
      setPlayer(prev => {
        const newY = prev.y + prev.velocityY;
        const newVelocityY = prev.velocityY + GRAVITY;

        if (newY > 340) { // Adjusted from 300 to match the ground
          setIsJumping(false);
          return {
            ...prev,
            y: 340, // Adjusted from 300 to match the ground
            velocityY: 0
          };
        }

        return {
          ...prev,
          y: newY,
          velocityY: newVelocityY
        };
      });

      // Move obstacles with current level speed
      setObstacles(prevObstacles => {
        const updatedObstacles = prevObstacles
          .map(obstacle => ({
            ...obstacle,
            x: obstacle.x - currentLevelConfig.speed
          }))
          .filter(obstacle => obstacle.x > -obstacle.width);

        // Check collisions
        updatedObstacles.forEach(obstacle => {
          const playerRect = {
            left: player.x + 5,
            right: player.x + player.width - 5,
            top: player.y + 5,
            bottom: player.y + player.height - 5
          };

          const obstacleRect = {
            left: obstacle.x + 5,
            right: obstacle.x + obstacle.width - 5,
            top: obstacle.y + 5,
            bottom: obstacle.y + obstacle.height - 5
          };

          if (
            playerRect.right > obstacleRect.left &&
            playerRect.left < obstacleRect.right &&
            playerRect.bottom > obstacleRect.top &&
            playerRect.top < obstacleRect.bottom
          ) {
            setGameOver(true);
            setGameStarted(false);
            if (score > highScore) {
              setHighScore(score);
            }
          }
        });

        return updatedObstacles;
      });

      // Move coins with current level speed
      setCoins(prevCoins => {
        return prevCoins
          .map(coin => ({
            ...coin,
            x: coin.x - currentLevelConfig.speed
          }))
          .filter(coin => {
            const coinRect = {
              left: coin.x,
              right: coin.x + coin.width,
              top: coin.y,
              bottom: coin.y + coin.height
            };

            const playerRect = {
              left: player.x,
              right: player.x + player.width,
              top: player.y,
              bottom: player.y + player.height
            };

            const collected = 
              playerRect.right > coinRect.left &&
              playerRect.left < coinRect.right &&
              playerRect.bottom > coinRect.top &&
              playerRect.top < coinRect.bottom;

            if (collected) {
              setScore(s => s + 10);
            }

            return !collected && coin.x > -coin.width;
          });
      });

    }, 1000 / 60);

    return () => clearInterval(gameLoop);
  }, [gameStarted, player.x, player.y, player.width, player.height, score, highScore, currentLevelConfig.speed]);

  // Spawn obstacles with current level frequency
  useEffect(() => {
    if (!gameStarted) return;

    const spawnObstacle = setInterval(() => {
      setObstacles(prev => [
        ...prev,
        {
          x: 800,
          y: 340, // Adjusted from 300 to match the ground
          width: 30,
          height: 50
        }
      ]);
    }, currentLevelConfig.obstacleFrequency);

    return () => clearInterval(spawnObstacle);
  }, [gameStarted, currentLevelConfig.obstacleFrequency]);

  // Spawn coins with current level frequency
  useEffect(() => {
    if (!gameStarted) return;

    const spawnCoin = setInterval(() => {
      setCoins(prev => [
        ...prev,
        {
          x: 800,
          // Adjusted coin spawn height to be reachable with max jump height
          // Now coins will spawn between y=240 (higher) and y=300 (lower)
          // This ensures coins are always reachable at the peak of a jump
          y: Math.random() * 60 + 240, // Changed from Math.random() * 100 + 150
          width: 20,
          height: 20
        }
      ]);
    }, currentLevelConfig.coinFrequency);

    return () => clearInterval(spawnCoin);
  }, [gameStarted, currentLevelConfig.coinFrequency]);

  const startGame = () => {
    setGameStarted(true);
    setGameOver(false);
    setScore(0);
    setObstacles([]);
    setCoins([]);
    setPlayer({
      x: 50,
      y: 340, // Adjusted from 300 to match the ground
      width: 40,
      height: 40,
      velocityY: 0
    });
    setShowTutorial(false);
    setShowLevelUp(false);
  };

  const startNextLevel = () => {
    setCurrentLevel(prev => prev + 1);
    startGame();
  };

  const handleBudgetSubmit = () => {
    // Calculate total allocation
    const total = budgetCategories.reduce((sum, category) => sum + category.allocation, 0);
    
    if (total !== score) {
      toast.error(`Your total budget must equal your coins (${score})`);
      return;
    }

    // Calculate savings percentage
    const savingsCategory = budgetCategories.find(cat => cat.name === "Savings");
    const savingsPercentage = (savingsCategory?.allocation || 0) / score * 100;

    if (savingsPercentage < 20) {
      toast.warning("Try to save at least 20% of your coins!");
      return;
    }

    setShowBudgetExercise(false);
    setShowLevelUp(true);
    toast.success("Great budgeting! You're ready for the next level!");
  };

  const handleBudgetChange = (categoryName: string, value: string) => {
    const numValue = value === "" ? 0 : parseInt(value);
    setBudgetCategories(prev => 
      prev.map(cat => 
        cat.name === categoryName 
          ? { ...cat, allocation: numValue } 
          : cat
      )
    );
  };

  return (
    <Card className="w-full max-w-[1200px] mx-auto">
      {/* Tutorial Modal */}
      <Dialog open={showTutorial} onOpenChange={setShowTutorial}>
        <DialogContent className="sm:max-w-[425px] bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-primary/20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <InfoIcon className="w-6 h-6 text-primary animate-bounce-small" />
              Welcome to Savings Sprint!
            </DialogTitle>
            <DialogDescription className="space-y-4 pt-4">
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg backdrop-blur-sm animate-fade-in">
                <PiggyBankIcon className="w-8 h-8 text-pink-500 animate-bounce-small" />
                <p>You control a wise piggy bank trying to save money!</p>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg backdrop-blur-sm animate-fade-in delay-100">
                <CoinsIcon className="w-8 h-8 text-yellow-500 animate-spin-slow" />
                <p>Collect coins to increase your savings score!</p>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg backdrop-blur-sm animate-fade-in delay-200">
                <ShoppingBagIcon className="w-8 h-8 text-red-500" />
                <p>Jump over shopping bags to avoid impulse purchases!</p>
              </div>

              <div className="flex items-center gap-3 p-3 bg-background/50 rounded-lg backdrop-blur-sm animate-fade-in delay-300">
                <SpaceIcon className="w-8 h-8 text-secondary" />
                <p>Press SPACE or tap/click to jump</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              onClick={startGame}
              size="lg"
              className="w-full sm:w-auto animate-pulse bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
            >
              Start Playing!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Level Up Modal */}
      <Dialog open={showLevelUp} onOpenChange={setShowLevelUp}>
        <DialogContent className="sm:max-w-[425px] bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-accent/20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <ArrowUpIcon className="w-8 h-8 text-yellow-500 animate-bounce-small" />
              Level {currentLevel} Complete!
            </DialogTitle>
            <DialogDescription className="space-y-4 pt-4">
              <div className="p-4 bg-white/50 rounded-lg backdrop-blur-sm text-center animate-fade-in">
                <TrophyIcon className="w-12 h-12 mx-auto text-yellow-500 animate-bounce-small mb-2" />
                <p className="text-lg font-semibold">Congratulations! 🎉</p>
                <p className="mt-2">You've saved {score} coins!</p>
                <p className="mt-2">Ready for Level {currentLevel + 1}?</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Next level: Faster obstacles and more coins needed!
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              onClick={startNextLevel}
              size="lg"
              className="w-full sm:w-auto animate-pulse bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
            >
              Start Level {currentLevel + 1}!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Budget Exercise Modal */}
      <Dialog open={showBudgetExercise} onOpenChange={setShowBudgetExercise}>
        <DialogContent className="sm:max-w-[425px] bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-primary/20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <SaveIcon className="w-8 h-8 text-primary animate-bounce-small" />
              Budget Your Coins!
            </DialogTitle>
            <DialogDescription className="space-y-4 pt-4">
              <div className="p-4 bg-white/50 rounded-lg backdrop-blur-sm text-center animate-fade-in">
                <CoinsIcon className="w-12 h-12 mx-auto text-yellow-500 animate-spin-slow mb-2" />
                <p className="text-lg font-semibold">You collected {score} coins! 🎉</p>
                <p className="mt-2">How would you budget these coins?</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Tip: Try to save at least 20% of your coins!
                </p>
              </div>

              <div className="space-y-4 animate-fade-in delay-100">
                {budgetCategories.map((category) => (
                  <div key={category.name} className="space-y-2">
                    <Label htmlFor={category.name}>{category.name}</Label>
                    <Input
                      id={category.name}
                      type="number"
                      min="0"
                      max={score}
                      value={category.allocation}
                      onChange={(e) => handleBudgetChange(category.name, e.target.value)}
                      className="w-full bg-white/50 backdrop-blur-sm border-2 focus-visible:ring-primary"
                    />
                  </div>
                ))}

                <div className="flex justify-between items-center pt-2 text-sm font-medium">
                  <span>Total:</span>
                  <span className={`font-bold ${
                    budgetCategories.reduce((sum, cat) => sum + cat.allocation, 0) === score 
                      ? 'text-green-500' 
                      : 'text-red-500'
                  }`}>
                    {budgetCategories.reduce((sum, cat) => sum + cat.allocation, 0)} / {score}
                  </span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              onClick={handleBudgetSubmit}
              size="lg"
              className="w-full sm:w-auto animate-pulse bg-gradient-to-r from-primary to-green-600 hover:from-primary/90 hover:to-green-700"
            >
              Submit Budget
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div 
        className="relative bg-gradient-to-b from-blue-200 via-blue-100 to-green-200 rounded-lg overflow-hidden shadow-xl h-[80vh]" 
      >
        {/* Game canvas */}
        <div className="absolute inset-0">
          {/* Player */}
          <div 
            className="absolute z-10"
            style={{
              left: `${player.x}px`,
              top: `${player.y}px`,
              width: `${player.width}px`,
              height: `${player.height}px`,
              transform: `rotate(${player.velocityY * 2}deg)`,
              transition: 'transform 0.1s ease-out'
            }}
          >
            <PiggyBankIcon className="w-full h-full text-pink-500 drop-shadow-lg animate-bounce-small" />
          </div>

          {/* Obstacles */}
          {obstacles.map((obstacle, index) => (
            <div
              key={index}
              className="absolute z-20"
              style={{
                left: `${obstacle.x}px`,
                top: `${obstacle.y}px`,
                width: `${obstacle.width}px`,
                height: `${obstacle.height}px`,
              }}
            >
              <ShoppingBagIcon className="w-full h-full text-red-500 drop-shadow-md" />
            </div>
          ))}

          {/* Coins */}
          {coins.map((coin, index) => (
            <div
              key={index}
              className="absolute z-30"
              style={{
                left: `${coin.x}px`,
                top: `${coin.y}px`,
                width: `${coin.width}px`,
                height: `${coin.height}px`,
              }}
            >
              <CoinsIcon className="w-full h-full text-yellow-500 drop-shadow-lg animate-spin-slow" />
            </div>
          ))}

          {/* Ground - adjusted position and height */}
          <div className="absolute bottom-0 w-full z-0">
            <div className="h-20 bg-gradient-to-b from-green-300 to-green-400" /> {/* Increased height from 16 to 20 */}
            <div className="h-4 bg-gradient-to-b from-brown-400 to-brown-500" />
          </div>
        </div>

        {/* Decorative clouds */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-10 left-1/4 w-20 h-8 bg-white rounded-full opacity-70 animate-float" />
          <div className="absolute top-20 right-1/3 w-16 h-6 bg-white rounded-full opacity-60 animate-float delay-1000" />
          <div className="absolute top-5 right-1/4 w-24 h-10 bg-white rounded-full opacity-80 animate-float delay-500" />
        </div>

        {/* Game overlay */}
        {!gameStarted && !showTutorial && !showLevelUp && (
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex flex-col items-center justify-center text-white z-50">
            <h2 className="text-4xl font-bold mb-4 flex items-center gap-2">
              {gameOver ? (
                <>
                  <TrophyIcon className="w-8 h-8 text-yellow-400 animate-bounce-small" />
                  Game Over!
                </>
              ) : (
                "Savings Sprint"
              )}
            </h2>
            {gameOver && (
              <div className="text-center mb-4 space-y-2">
                <p className="text-xl flex items-center justify-center gap-2">
                  <CoinsIcon className="w-6 h-6 text-yellow-500" />
                  Score: {score}
                </p>
                <p className="text-xl flex items-center justify-center gap-2">
                  <TrophyIcon className="w-6 h-6 text-yellow-500" />
                  High Score: {highScore}
                </p>
                <p className="text-lg mt-2 flex items-center justify-center gap-2">
                  <ArrowUpIcon className="w-6 h-6 text-green-500" />
                  Level Reached: {currentLevel}
                </p>
              </div>
            )}
            <Button 
              size="lg"
              onClick={startGame}
              className="animate-bounce-small bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
            >
              {gameOver ? "Play Again" : "Start Game"}
            </Button>
          </div>
        )}

        {/* Score and level display */}
        {gameStarted && (
          <div className="absolute top-4 left-4 space-y-2 z-40">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2 flex items-center gap-2 shadow-lg animate-fade-in">
              <CoinsIcon className="w-6 h-6 text-yellow-500 animate-spin-slow" />
              <span className="font-bold text-lg">{score}</span>
            </div>
            <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg animate-fade-in delay-100">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium">Level {currentLevel}</span>
                <span className="text-sm text-muted-foreground">
                  {score}/{currentLevelConfig.requiredCoins}
                </span>
              </div>
              <Progress value={levelProgress} className="h-2" />
            </div>
          </div>
        )}
      </div>

      {/* Game instructions */}
      <div className="mt-4 text-center text-sm text-muted-foreground">
        <p>Press SPACE or tap/click to jump. Collect coins and avoid shopping bags!</p>
        <p>Save enough coins to advance to the next level!</p>
      </div>
    </Card>
  );
};
